<script lang="ts">
  import type { SkillCategory } from '$lib/api/types';
  import { loadImage } from '$lib/util';
  import { faImage } from '@itdev-fortawesome/pro-solid-svg-icons/faImage';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import FormModal from '../common/modal/FormModal.svelte';
  import { createSkillCategory, deleteSkillCategory, updateSkillCategory } from '$lib/api/courses';
  import Label from '../common/bootstrap/Label.svelte';
  import Input from '../common/bootstrap/Input.svelte';

  interface Props {
    courseId: number;
    skillCategory: SkillCategory | null;
    onClose: () => void;
  }

  let { courseId, skillCategory, onClose }: Props = $props();

  // canvas size is double the size rendered in the dom to make the canvas and image crisp on high-res displays
  const canvasSize = 256;

  let fileChooser: HTMLInputElement;
  let canvas: HTMLCanvasElement | null = $state(null);
  let img: string | null = skillCategory?.badge ?? null;
  let newFileObjectUrl: string | null = $state(null);

  let categoryName = $state(skillCategory?.name ?? '');
  let categoryNameTouched = $state(false);
  let badgeInvalidFeedback: string | null = $state(skillCategory?.badge ? null : 'No image selected.');
  let badgeTouched = $state(false);

  async function addImageToCanvas(file: File) {
    newFileObjectUrl = URL.createObjectURL(file);
    const canvasImg = await loadImage(newFileObjectUrl);
    const intrinsicWidth = canvasImg.naturalWidth;
    const intrinsicHeight = canvasImg.naturalHeight;

    if (intrinsicWidth < canvasSize || intrinsicHeight < canvasSize) {
      badgeInvalidFeedback = `File must be at least ${canvasSize}px tall or wide. Selected image dimensions: ${intrinsicWidth} x ${intrinsicHeight}`;
      newFileObjectUrl = null;
      return;
    }

    const ratio = intrinsicWidth / intrinsicHeight;
    let scaledHeight = canvasSize;
    let scaledWidth = canvasSize;
    if (ratio >= 1) {
      // wider than tall
      scaledHeight = (scaledHeight / intrinsicWidth) * intrinsicHeight;
    } else {
      // taller than wide
      scaledWidth = (scaledWidth / intrinsicHeight) * intrinsicWidth;
    }
    // center the image
    const dx = (canvasSize - scaledWidth) / 2;
    const dy = (canvasSize - scaledHeight) / 2;
    const drawCtx = canvas!.getContext('2d')!;
    drawCtx.imageSmoothingEnabled = true;
    drawCtx.imageSmoothingQuality = 'high';
    drawCtx.clearRect(0, 0, canvasSize, canvasSize);
    drawCtx.drawImage(canvasImg, dx, dy, scaledWidth, scaledHeight);

    badgeInvalidFeedback = null;
  }
</script>

<FormModal
  formId="skill_category_editor"
  invalidateUrl={url => url.pathname.startsWith('/api/courses/')}
  header="Category"
  onDelete={skillCategory
    ? async () => {
        await deleteSkillCategory(courseId!, skillCategory!.id);
      }
    : undefined}
  deleteConfirmText="Delete category?"
  onclose={onClose}
  onsubmit={async () => {
    if (skillCategory) {
      await updateSkillCategory(courseId, skillCategory!.id, {
        name: categoryName,
        badge: canvas === null ? skillCategory!.badge : canvas!.toDataURL('image/webp', 0.95),
      });
    } else {
      await createSkillCategory(courseId, {
        name: categoryName,
        badge: canvas!.toDataURL('image/webp', 0.95),
      });
    }
    return true;
  }}
  validationChecks={() => {
    categoryNameTouched = true;
    badgeTouched = true;
    return categoryName.length > 0 && categoryName.length <= 128 && badgeInvalidFeedback === null;
  }}
>
  <div class="mb-3">
    <Label for="skill_category_name">Name</Label>
    <Input
      bind:value={categoryName}
      required
      maxlength={128}
      type="text"
      id="skill_category_name"
      placeholder="Category Name"
      invalid={categoryNameTouched && (categoryName.length === 0 || categoryName.length > 128)}
      onblur={() => (categoryNameTouched = true)}
      feedback="Category Name is required and must be 128 characters or less."
    />
  </div>
  <div>
    <Label for="badge_file_picker">Badge</Label>
    <div class="d-flex align-items-center gap-3">
      <button
        type="button"
        aria-label="Click to choose image"
        class="btn badge-container"
        class:invalid={badgeTouched && badgeInvalidFeedback !== null}
        onclick={() => {
          fileChooser.click();
        }}
      >
        {#if newFileObjectUrl !== null}
          <canvas
            height={canvasSize}
            width={canvasSize}
            bind:this={canvas}
            style="height: {canvasSize / 2}px; width: {canvasSize / 2}px;"
          ></canvas>
        {:else if img !== null}
          <img alt="current skill category badge" src={img} />
        {:else}
          <FontAwesomeIcon icon={faImage} />
        {/if}
      </button>
      <input
        accept="image/*"
        id="badge_file_picker"
        aria-describedby="image_contraints_description"
        aria-invalid={badgeInvalidFeedback !== null ? 'true' : 'false'}
        aria-errormessage={badgeInvalidFeedback !== null ? 'badge-invalid-feedback' : undefined}
        type="file"
        hidden
        bind:this={fileChooser}
        oncancel={() => {
          badgeTouched = true;
          badgeInvalidFeedback = 'No image selected.';
        }}
        onchange={async e => {
          badgeTouched = true;
          const trg = e.target as HTMLInputElement;
          if (!trg.files || trg.files.length === 0) {
            badgeInvalidFeedback = 'No image selected.';
            return;
          }

          const file = trg.files[0];
          if (!file.type.startsWith('image')) {
            badgeInvalidFeedback = 'File must be an image.';
            return;
          }

          await addImageToCanvas(file);
        }}
      />
      <p class="text-secondary fst-italic small mb-0" id="image_contraints_description">
        PNG or WEBP image formats with transparent backgrounds preferred. Image will be scaled down to {canvasSize}px
        square and rendered as WEBP when uploaded.
      </p>
    </div>
    {#if badgeTouched && badgeInvalidFeedback !== null}
      <div class="invalid-feedback d-block" id="badge-invalid-feedback">{badgeInvalidFeedback}</div>
    {/if}
  </div>
</FormModal>

<style>
  .badge-container {
    min-width: calc(130px + 1rem);
    min-height: calc(130px + 1rem);
    width: calc(130px + 1rem);
    height: calc(130px + 1rem);
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--bs-border-radius);
    border: var(--bs-border-width) solid var(--bs-border-color);

    --bs-btn-bg: white;
    --bs-btn-color: var(--bs-secondary);
    --bs-btn-hover-bg: var(--bs-secondary-bg);
  }

  .badge-container img {
    max-width: 128px;
    max-height: 128px;
  }

  .badge-container.invalid {
    border-color: var(--bs-form-invalid-border-color);
  }
</style>
